import { GetColorDefinition } from 'atoms/colors/colors';
import { font } from 'atoms/typography/typography';

import Container from 'quarks/Container';
import Flex from 'quarks/Flex';
import Heading from 'quarks/Heading';
import Image from 'quarks/Image';
import Text from 'quarks/Text';
import type { BasicProps } from 'quarks/interpolations/basic';

// import Link from 'molecules/Link';

import richTextParser, { RichText } from 'utils/richTextParser';

import type { IComponentFeatureRecord, IComponentStatisticsPanelRecord } from 'graphql/types';
import type { FC } from 'react';

type TextStyles = keyof typeof font.display;

interface StatisticsPanelProps extends BasicProps, IComponentStatisticsPanelRecord {
  backgroundIsDark: boolean;
  background: string;
  statisticsBody?: IComponentStatisticsPanelRecord['bodyCopy'];
  inContent: boolean;
}

interface FeatureSectionProps extends BasicProps {
  featureList: IComponentFeatureRecord[];
  isInStatGrids: boolean;
}

const StatisticsPanel: FC<StatisticsPanelProps> = ({
  backgroundIsDark,
  background,
  headline,
  headlineSizeDesktop,
  headlineSizeMobile,
  eyebrowIcon,
  statisticsBody,
  variant,
  statGrids,
  features,
  featuredImage,
  reverse,
  alignment,
  inContent,
}) => {
  const getEyebrowColor = (isInStatGrids: boolean) => {
    if (background === 'blue-light') {
      return 'wave-800';
    }

    return backgroundIsDark ? `${isInStatGrids ? 'foam' : 'bubbles'}-50` : `storm-${isInStatGrids ? 600 : 900}`;
  };

  const FeatureSection: FC<FeatureSectionProps> = ({ featureList, isInStatGrids }) => (
    <Flex
      flexDirection="column"
      marginX="auto"
      flexWrap={featureList.length > 3 ? 'wrap' : 'nowrap'}
      rowGap="32px"
      sm={{
        flexDirection: 'row',
        gap: isInStatGrids ? '64px 32px' : '20px',
        alignItems: 'start',
      }}
      md={{ gap: isInStatGrids ? '64px 32px' : '80px' }}
    >
      {featureList.map(feature => (
        <Container
          key={feature.id}
          maxWidth="1040px"
          sm={{ width: !isInStatGrids ? 100 / featureList.length + '%' : '200px' }}
          md={{ width: '200px' }}
          lg={{ width: !isInStatGrids ? 100 / featureList.length + '%' : '200px' }}
        >
          <Flex
            flexDirection="column"
            alignItems={alignment === 'left' ? 'left' : 'center'}
            textAlign={alignment === 'left' ? 'left' : 'center'}
            marginX="auto"
            md={{ maxWidth: !isInStatGrids ? '100%' : '200px' }}
          >
            {feature.icon?.url && (
              <Text
                marginBottom={20}
                display="block"
                width={(() => {
                  if (feature.iconSize === 'large') {
                    return '200px';
                  } else if (feature.iconSize === 'medium') {
                    return '80px';
                  } else {
                    return '48px';
                  }
                })()}
                height={(() => {
                  if (feature.iconSize === 'large') {
                    return '200px';
                  } else if (feature.iconSize === 'medium') {
                    return '80px';
                  } else {
                    return '48px';
                  }
                })()}
                position="relative"
              >
                {feature.iconLink && feature.iconLink !== '' ? (
                  <a href={feature.iconLink}>
                    <Image
                      src={feature.icon.url}
                      alt=""
                      width="100%"
                      height="100%"
                      objectFit="contain"
                      objectPosition="center"
                    />
                  </a>
                ) : (
                  <Image
                    src={feature.icon.url}
                    alt=""
                    width="100%"
                    height="100%"
                    objectFit="contain"
                    objectPosition="center"
                  />
                )}
              </Text>
            )}
            {feature.stat && (
              <Heading
                as="h4"
                fontSize="displayMd"
                lineHeight="sm"
                marginBottom={16}
                fontWeight="bold"
                textColor={backgroundIsDark ? 'common-white' : 'wave-800'}
                sm={{ fontSize: 'displayLg' }}
              >
                {feature.stat}
              </Heading>
            )}
            {feature.eyebrow && (
              <Text
                textTransform="uppercase"
                textStyle="sm"
                fontWeight="bold"
                marginBottom={16}
                textColor={getEyebrowColor(isInStatGrids) as GetColorDefinition}
              >
                {feature.eyebrow}
              </Text>
            )}
            {feature.headline && (
              <Heading
                as="h3"
                fontSize={inContent ? 'displayXs' : 'displayXs'}
                fontWeight="bold"
                textColor={backgroundIsDark ? 'foam-50' : `${feature.icon?.url ? 'storm-900' : 'wave-800'}`}
                lg={{ fontWeight: 'bold' }}
                marginBottom={16}
              >
                {feature.headline}
              </Heading>
            )}

            {feature.bodyCopy &&
              feature.bodyCopy.value &&
              feature.bodyCopy.value.document &&
              feature.bodyCopy.value.document.children &&
              feature.bodyCopy.value.document.children.some(
                (child: { children: { type: string; value: string }[] }) =>
                  child.children && child.children.some(innerChild => innerChild.value),
              ) && (
                <Container
                  fontSize={inContent ? 'textLg' : 'textXl'}
                  textColor={backgroundIsDark ? `${isInStatGrids ? 'storm-100' : 'foam-50'}` : 'storm-600'}
                  textAlign={alignment === 'left' ? 'left' : 'center'}
                  maxWidth="275px"
                  width="100%"
                  css={{
                    p: {
                      '&:first-child': {
                        marginBottom: 32,
                      },
                    },
                    '.MuiButton-root': {
                      '&:first-child': {
                        marginRight: 16,
                      },
                    },
                  }}
                >
                  {richTextParser(feature.bodyCopy as RichText, backgroundIsDark, false)}
                </Container>
              )}
          </Flex>
        </Container>
      ))}
    </Flex>
  );

  return (
    <Container>
      <Flex
        flexDirection="column"
        flexWrap="nowrap"
        textAlign="center"
        alignItems="center"
        textColor={backgroundIsDark ? 'common-white' : 'storm-900'}
      >
        {eyebrowIcon && (
          <Text marginBottom={32}>
            <Image src={eyebrowIcon.url} alt="" />
          </Text>
        )}
        {headline && (
          <Heading
            as="h2"
            maxWidth="880px"
            marginX="auto"
            textStyle={(headlineSizeMobile as TextStyles) || 'md'}
            md={{ textStyle: (headlineSizeDesktop as TextStyles) || 'lg', fontWeight: 'bold' }}
            fontWeight="bold"
          >
            {headline}
          </Heading>
        )}
        {statisticsBody &&
          statisticsBody.value &&
          statisticsBody.value.document &&
          statisticsBody.value.document.children &&
          statisticsBody.value.document.children.some(
            (child: { children: { type: string; value: string }[] }) =>
              child.children && child.children.some(innerChild => innerChild.value),
          ) && (
            <Flex
              fontSize="textXl"
              marginBottom={features && features.length > 0 ? 24 : 0}
              textColor={backgroundIsDark ? 'common-white' : 'storm-900'}
              maxWidth="880px"
              marginX="auto"
              marginTop={12}
              md={{ fontSize: 'textXl' }}
            >
              {richTextParser(statisticsBody as RichText, backgroundIsDark, false)}
            </Flex>
          )}
        <Flex flexDirection={reverse ? 'column-reverse' : 'column'} marginTop={headline !== '' ? 48 : 0}>
          {variant === 'default' && features && features.length > 0 && (
            <FeatureSection featureList={features} isInStatGrids={false} />
          )}
          {variant === 'default' && featuredImage && (
            <Flex justifyContent="center" textAlign="center" width="100%" marginX="auto" marginTop={48}>
              {featuredImage?.url?.includes('.mp4') || featuredImage?.url?.includes('.webm') ? (
                <Container width="100%" position="relative">
                  <video
                    src={featuredImage?.url}
                    title={featuredImage?.title || ''}
                    width="100%"
                    height="100%"
                    autoPlay
                    loop
                    muted
                    playsInline
                    controls={false}
                  />
                </Container>
              ) : (
                <Image
                  width="100%"
                  height="100%"
                  objectFit="contain"
                  objectPosition="center"
                  marginX="auto"
                  src={featuredImage.url}
                  alt={featuredImage.title || ''}
                  responsiveImage={featuredImage.responsiveImage || undefined}
                  inlineStyle={{
                    maxWidth: '100%',
                    maxHeight: '660px',
                  }}
                />
              )}
            </Flex>
          )}
        </Flex>
      </Flex>
      {variant === 'split' &&
        statGrids!.length > 0 &&
        statGrids?.map(statItem => (
          <Flex
            key={statItem.id}
            marginX="auto"
            flexDirection="column"
            rowGap="72px"
            paddingTop={48}
            paddingBottom={24}
            lg={{ flexDirection: statItem.reverse ? 'row-reverse' : 'row', alignItems: 'center', rowGap: '0' }}
          >
            <Flex
              display="block"
              flex="unset"
              sm={{ maxWidth: '464px', marginX: 'auto' }}
              lg={{ flex: '0 0 50%', width: '50%', maxWidth: '100%' }}
            >
              {statItem.features && <FeatureSection featureList={statItem.features} isInStatGrids={true} />}
            </Flex>
            <Flex
              textAlign="center"
              alignItems="center"
              flex="unset"
              justifyContent="center"
              lg={{
                flex: '0 0 50%',
                width: '50%',
                maxWidth: '100%',
                justifyContent: statItem.reverse ? 'start' : 'end',
              }}
            >
              {statItem.featuredImage && (
                <Image
                  maxWidth="100%"
                  src={statItem.featuredImage.url}
                  alt={statItem.featuredImage.title as string}
                  responsiveImage={statItem.featuredImage.responsiveImage || undefined}
                  sm={{ maxWidth: '570px' }}
                />
              )}
            </Flex>
          </Flex>
        ))}
      {variant === 'default' &&
        statGrids!.length > 0 &&
        statGrids?.map(statItem => (
          <Flex
            key={statItem.id}
            marginX="auto"
            flexDirection="column"
            rowGap="72px"
            paddingTop={48}
            paddingBottom={24}
            lg={{ flexDirection: statItem.reverse ? 'row-reverse' : 'row', alignItems: 'center', rowGap: '0' }}
          >
            <Flex
              display="block"
              flex="unset"
              sm={{ maxWidth: '464px', marginX: 'auto' }}
              lg={{ flex: '0 0 50%', width: '50%', maxWidth: '100%' }}
            >
              {statItem.features && <FeatureSection featureList={statItem.features} isInStatGrids={true} />}
            </Flex>
          </Flex>
        ))}
    </Container>
  );
};

export default StatisticsPanel;
